import React, { Dispatch, SetStateAction, useState } from 'react'
import { SupplierOperation, OpProduct } from '../../core/_models'
import { exportBulkOrder, generateSupplyFile, revertSplit } from '../../core/_requests'
import {StateForm} from './StateForm'
import UploadVouchers from './UploadVouchers'
import Timeline from './Timeline'
import {ExportCsvButton} from '@/components/utils/export'
import {useIntl} from 'react-intl'

type PaneProps = {
  priceDigits: number
  order: SupplierOperation
  contacts: string[]
  setOrder: Dispatch<SetStateAction<SupplierOperation | undefined>>
}

type DProps = {
  label: string
  value: number
  isAmount: boolean
  color: string
}

const RenderDetails : React.FC<DProps>= ({label, value , isAmount, color}) => {
  const intl = useIntl()
  return (
    <div className="d-flex align-items-center mb-3">
      <span className={`bullet bullet-vertical h-20px bg-${color}`}></span>
      <div className="flex-grow-1 ps-2">
        <span className="text-gray-800 text-hover-info fw-bold fs-6">{label}</span>
      </div>
      <span className={`badge badge-light-${color} fs-8 fw-bold`}>
        {isAmount ? intl.formatNumber(value,  {style: 'currency', currency: 'EUR'}) : value}
      </span>
    </div>
  )
}

const SidePane: React.FC<PaneProps> = ({order, setOrder, priceDigits, contacts}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const exportCsv = async () => await exportBulkOrder(order?._id, 'csv')
  const exportSupplyCsv = async () => await generateSupplyFile(order?._id, 'csv')
  const currentState = order?.states?.at(-1)
  const isMutable =
    !['PAID', 'REJECTED', 'PROVIDED', 'PROCESSED', 'PROCESSING', 'SPLITTED'].includes(currentState?.state || 'INIT')
    && order.parent === undefined
  const delivred = (order?.products ?? []).reduce((acc: number, curr: OpProduct) => acc + curr.delivred, 0)
  const details = {
    qty: 0,
    amount: 0,
    discount: 0
  }

  const handleRevertSplit = async () => {
    try {
      setIsLoading(true);
      const res = await revertSplit(order?._id);

      if (res) {
        setOrder(res);
      }
    } catch (e: unknown) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  }

  order?.products?.forEach((o: OpProduct) => {
    const discountedUnitPrice = parseFloat(
      (o.amount * (100 - o.discount) / 100).toFixed(priceDigits)
    )

    details.qty += o.qty
    details.amount += o.amount * o.qty
    details.discount += (o.amount * o.qty) - (discountedUnitPrice * o.qty)
  });

  return (
    <div className="m-0">
      <div className="d-print-none border border-dashed border-gray-300 card-rounded h-lg-100 min-w-md-350px p-9 bg-lighten">
        <h6 className="mb-4 fw-bolder text-gray-600 text-center">
          ORDER OVERVIEW
        </h6>
        <div className='separator separator-dashed my-5'></div> 

        <RenderDetails label='Total products' value={details.qty} isAmount={false} color="default" />
        <RenderDetails label='Base amount' value={details.amount} isAmount={true} color="info" />
        <RenderDetails label='Discount' value={details.discount} isAmount={true} color="primary" />
        <RenderDetails label='Order amount' value={details.amount - details.discount} isAmount={true} color="success" />

        <div className='separator separator-dashed my-2'></div> 

        <div className="d-flex align-items-center mb-3">
          <div className="flex-grow-1 fw-bold">PO#</div>
          <span className="badge badge-secondary fs-8 fw-bold">
            {order?.po_number || '-'}
          </span>
        </div>

        <div className="d-flex align-items-center mb-3">
          <div className="flex-grow-1 fw-bold">Payment#</div>
          <span className="badge badge-light fs-8 fw-bold">
            {order?.pay_ref || '-'}
          </span>
        </div>

        <div className='separator separator-dashed my-5'></div> 

        <div className="text-center">
          <ExportCsvButton
            request={exportCsv}
            fileName={order.reference || ''}
          />
          {order?.po_number && delivred === 0 && (
            <ExportCsvButton
              className="btn btn-sm btn-primary ms-5"
              request={exportSupplyCsv}
              label="Supply file"
              fileName={`supply-${order.po_number}`}
            />
          )}
        </div>
        {contacts.length > 0 && (
          <>
            <div className='separator separator-dashed my-5'></div> 
            <h6 className="mb-4 fw-bolder text-gray-600 text-center">
              CONTACTS
            </h6>
            <div className='separator separator-dashed my-5'></div> 
            <ul>
              {contacts.map((contact: string) => (
                <li className="d-flex align-items-center py-2" key={contact}>
                  <span className="bullet bg-primary me-5"></span>
                  <span className="text-gray-800 fw-bold fs-6">{contact}</span>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className='separator separator-dashed my-5'></div> 

        <Timeline order={order}  setOrder={setOrder} />

        {isMutable ? (
          <>
            <div className='separator separator-dashed my-5'></div> 
            <div>
              <StateForm order={order} setOrder={setOrder}/>
            </div>
          </>
        ) : (currentState?.state === 'PAID'  || (order?.parent && currentState?.state === 'INIT')) && (
          <>
            <div className='separator separator-dashed my-5'></div> 
            <UploadVouchers order={order} setOrder={setOrder} />
          </>
        )}

        {currentState?.state === 'SPLITTED' && order?.childs && order.childs.length > 0 && delivred === 0 && (
          <>
            <div className='separator separator-dashed my-2'></div> 
            <div className="text-center mt-5">
              <button
                className="btn btn-danger"
                type="button"
                disabled={isLoading}
                onClick={handleRevertSplit}
              >
                <span className='indicator-label'>
                  Revert split
                </span>
                {(isLoading) && (
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default SidePane
